import React from "react";
import { FiAlertTriangle, FiCalendar, FiCheckCircle, FiChevronRight } from "react-icons/fi";
import ComponentLoader from "./ComponentLoader";

const parseDateOnly = (value) => {
  if (!value) {
    return new Date();
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const formatAttendanceShortDate = (value) => {
  const date = parseDateOnly(value);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
  });
};

const formatAttendanceDateKey = (value) => {
  const date = parseDateOnly(value);
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Asia/Kolkata",
    year: "numeric",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return `${year}-${month}-${day}`;
};

const formatUnknownAttendanceDate = (row = {}) => {
  const date = row.date instanceof Date && !Number.isNaN(row.date.getTime())
    ? row.date
    : parseDateOnly(row.dateKey || row.rawLoggedDate || row.loggedDate || row.date);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const isAttendancePresent = (attendance = {}) => {
  const todayKey = formatAttendanceDateKey(new Date());
  const attendanceKey = formatAttendanceDateKey(attendance.rawLoggedDate || attendance.loggedDate || attendance.date || new Date());
  return attendanceKey === todayKey && Boolean(attendance.status === "checked-in" || attendance.checkInAt || attendance.checkInTime);
};

const AttendanceCard = ({
  attendance,
  attendanceMarkingAllowed = true,
  attendanceNetworkMessage = "",
  attendanceNetworkResolved = true,
  loading = false,
  loadingLabel = "Saving attendance",
  onToggle,
  onViewMore,
  unknownAttendanceRows = [],
}) => {
  const isPresent = isAttendancePresent(attendance);
  const presentDate = attendance.rawLoggedDate || attendance.loggedDate || attendance.date || new Date();
  const unknownAttendanceDateText = unknownAttendanceRows
    .slice(0, 6)
    .map(formatUnknownAttendanceDate)
    .join(", ");
  const hiddenUnknownDateCount = Math.max(unknownAttendanceRows.length - 6, 0);
  const attendanceAccessChecking = !isPresent && !attendanceNetworkResolved;
  const attendanceRestricted = !isPresent && attendanceNetworkResolved && !attendanceMarkingAllowed;
  const attendanceActionDisabled = loading || isPresent || attendanceAccessChecking || attendanceRestricted;
  const networkNotice = attendanceAccessChecking
    ? "Checking the office network before attendance can be marked."
    : attendanceRestricted
      ? attendanceNetworkMessage || "Attendance can only be marked from the Antrazal office network."
      : "";

  return (
    <>
      <section className={`new-hrm-panel new-hrm-attendance-priority new-hrm-loader-host${isPresent ? " new-hrm-attendance-priority--present" : ""}`}>
        <ComponentLoader active={loading} label={loadingLabel} />
        <div className="new-hrm-attendance-priority__title">
          <span className={isPresent ? "new-hrm-tone--green" : "new-hrm-tone--blue"}>
            <FiCheckCircle aria-hidden="true" />
          </span>
          <div>
            <p>Attendance</p>
            <h2>{isPresent ? "Present marked" : "Mark attendance"}</h2>
            <small>{isPresent ? "Your attendance is marked present for today." : "Mark yourself present before starting work."}</small>
          </div>
        </div>

        <div className="new-hrm-attendance-priority__control">
          <span className={`new-hrm-status ${isPresent ? "new-hrm-status--approved" : "new-hrm-status--pending"}`}>
            <span className="new-hrm-attendance-status-dot" aria-hidden="true"></span>
            {isPresent ? "Present" : "Not marked"}
          </span>
          <div className="new-hrm-attendance-priority__control-row">
            <div className={`new-hrm-attendance-live ${isPresent ? "new-hrm-attendance-live--active" : ""}`}>
              {isPresent ? (
                <span className="new-hrm-attendance-live__pulse" aria-hidden="true"></span>
              ) : (
                <span className="new-hrm-attendance-live__icon" aria-hidden="true">
                  <FiCalendar />
                </span>
              )}
              <div>
                <small>{isPresent ? "Attendance code" : "Today"}</small>
                <strong>{isPresent ? "P" : formatAttendanceShortDate(new Date())}</strong>
                <span>{isPresent ? formatAttendanceShortDate(presentDate) : "Not marked"}</span>
                {onViewMore && (
                  <button type="button" className="new-hrm-attendance-history-link" onClick={onViewMore}>
                    <FiCalendar aria-hidden="true" />
                    View attendance
                  </button>
                )}
              </div>
            </div>
            {!isPresent && (
              <button
                aria-describedby={networkNotice ? "attendance-network-notice" : undefined}
                className="new-hrm-attendance-cta"
                disabled={attendanceActionDisabled}
                onClick={onToggle}
                title={networkNotice || "Mark attendance"}
                type="button"
              >
                <FiCheckCircle aria-hidden="true" />
                <span>Present</span>
              </button>
            )}
          </div>
          {networkNotice && (
            <p className="new-hrm-attendance-network-note" id="attendance-network-notice" role="status">
              <FiAlertTriangle aria-hidden="true" />
              <span>{networkNotice}</span>
            </p>
          )}
        </div>
      </section>

      {unknownAttendanceRows.length > 0 && (
        <section className="new-hrm-attendance-dashboard-warning" role="alert">
          <FiAlertTriangle aria-hidden="true" />
          <div>
            <strong>Attendance violation. Immediate action required.</strong>
            <p>
              {unknownAttendanceRows.length} unknown attendance day{unknownAttendanceRows.length === 1 ? "" : "s"} found this month. Your record is non-compliant.
              {unknownAttendanceDateText ? ` Unknown dates: ${unknownAttendanceDateText}${hiddenUnknownDateCount ? `, +${hiddenUnknownDateCount} more` : ""}.` : " Dates are unknown in the attendance record."}
              Unresolved unknown attendance can be treated as absence or LOP and escalated for disciplinary action. Fix it now from Leave and Attendance.
            </p>
            {onViewMore && (
              <button type="button" className="new-hrm-attendance-warning-link" onClick={onViewMore}>
                Open attendance history
                <FiChevronRight aria-hidden="true" />
              </button>
            )}
          </div>
        </section>
      )}

    </>
  );
};

export default AttendanceCard;
